import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'

import AdminPanel from '@/views/admin/AdminPanel.vue'
import LoginPage from '@/views/auth/LoginPage.vue'
import RegisterPage from '@/views/auth/RegisterPage.vue'
import AgentDetailPage from '@/views/customer/AgentDetailPage.vue'
import AgentsCenterPage from '@/views/customer/AgentsCenterPage.vue'
import DashboardPage from '@/views/customer/DashboardPage.vue'
import GenerationWorkspacePage from '@/views/customer/GenerationWorkspacePage.vue'
import KnowledgeBasePage from '@/views/customer/KnowledgeBasePage.vue'
import LandingPage from '@/views/customer/LandingPage.vue'
import LearningCenterPage from '@/views/customer/LearningCenterPage.vue'
import SettingsPage from '@/views/customer/SettingsPage.vue'
import SubmitRequirementPage from '@/views/customer/SubmitRequirementPage.vue'
import TaskSquarePage from '@/views/customer/TaskSquarePage.vue'
import { useUserStore } from '@/stores/user'
import { buildLoginLocation } from '@/utils/authNavigation'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Landing',
    component: LandingPage,
    meta: { title: '首页' },
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { title: '登录', guestOnly: true },
  },
  {
    path: '/register',
    name: 'Register',
    component: RegisterPage,
    meta: { title: '注册', guestOnly: true },
  },
  {
    path: '/submit',
    name: 'SubmitRequirement',
    component: SubmitRequirementPage,
    meta: { title: '提交需求', requiresAuth: true },
  },
  {
    path: '/dashboard',
    name: 'CustomerDashboard',
    component: DashboardPage,
    meta: { title: '项目进度', requiresAuth: true },
  },
  {
    path: '/task-square',
    name: 'TaskSquare',
    component: TaskSquarePage,
    meta: { title: '任务广场', requiresAuth: true },
  },
  {
    path: '/approval/:projectId',
    name: 'LegacyApprovalRedirect',
    redirect: (to) => ({ path: `/generation/${to.params.projectId}`, query: to.query }),
    meta: { title: '生成工作台', requiresAuth: true },
  },
  {
    path: '/generation/:projectId',
    name: 'GenerationWorkspace',
    component: GenerationWorkspacePage,
    meta: { title: '项目工作台', requiresAuth: true },
  },
  {
    path: '/settings',
    name: 'Settings',
    component: SettingsPage,
    meta: { requiresAuth: true, requiresAdmin: true, title: '模型与密钥配置' }
  },
  {
    path: '/knowledge',
    name: 'KnowledgeBase',
    component: KnowledgeBasePage,
    meta: { requiresAuth: true, title: '素材参考中心' }
  },
  {
    path: '/content-library',
    name: 'SystemContentLibrary',
    redirect: { path: '/knowledge', query: { tab: 'content' } },
    meta: { requiresAuth: true, title: '素材参考中心' }
  },
  {
    path: '/learning',
    name: 'LearningCenter',
    component: LearningCenterPage,
    meta: { requiresAuth: true, title: '自成长智能体' }
  },
  {
    path: '/workflow/:id',
    name: 'WorkflowOrchestrator',
    redirect: (to) => ({ path: `/generation/${to.params.id}`, query: to.query }),
    meta: { requiresAuth: true, title: '项目工作台' }
  },
  {
    path: '/agents',
    name: 'AgentsCenter',
    component: AgentsCenterPage,
    meta: { requiresAuth: true, title: '智能体中心' }
  },
  {
    path: '/agent/:agentName',
    name: 'AgentDetail',
    component: AgentDetailPage,
    meta: { requiresAuth: true, title: '智能体详情' }
  },
  {
    path: '/admin',
    name: 'AdminPanel',
    component: AdminPanel,
    meta: { requiresAuth: true, requiresAdmin: true, title: '管理后台' }
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Navigation guards
router.beforeEach((to, from, next) => {
  const userStore = useUserStore()
  
  // Load user from storage if exists
  if (!userStore.userInfo && localStorage.getItem('userInfo')) {
    userStore.loadFromStorage()
  }

  // Set page title
  if (to.meta.title) {
    document.title = `${to.meta.title} - videocore`
  }

  // Check authentication
  if (to.meta.requiresAuth && !userStore.isAuthenticated) {
    next(buildLoginLocation(to.fullPath))
    return
  }

  // Check guest-only routes (login/register)
  if (to.meta.guestOnly && userStore.isAuthenticated) {
    next({ name: 'CustomerDashboard' })
    return
  }

  // Check admin access
  if (to.meta.requiresAdmin && !userStore.canAccessAdmin) {
    next({ name: 'CustomerDashboard' })
    return
  }

  next()
})

export default router
